<template>
  <!-- Two-sided bar: dark studio half on the left, light portfolio half on the right. -->
  <header :class="['home-header', { expanded }]">
    <nav class="side-link studio">
      <router-link :to="{ name: 'FluorineRepublika' }">Fluorine Republika</router-link>
    </nav>
    <div class="center-content">
      <a class="name first" href="/home" @click.prevent="onBrandClick">Florin</a>
      <a class="logo-link" href="/home" @click.prevent="onBrandClick">
        <img class="logo" alt="logo" src="@/assets/images/logo.png">
      </a>
      <a class="name last" href="/home" @click.prevent="onBrandClick">Lica</a>
      <div class="links">
        <a class="small first" href="#contact" @click.prevent="scrollToContact">Contact</a>
        <span class="small dot" aria-hidden="true">·</span>
        <button class="small last" @click="goBack">Back</button>
      </div>
    </div>
    <nav class="side-link work">
      <router-link :to="{ name: 'OtherWork' }">Portfolio</router-link>
    </nav>
  </header>
</template>

<script>
// Touch screens and the phone layout open the menu by tapping instead of hovering.
const isTouch = () => window.matchMedia('(hover: none), (max-width: 768px)').matches;

export default {
  name: 'MainHeader',
  props: {
    // Kept so existing pages can keep passing them; the bar looks the same everywhere now.
    pageName: {
      type: String,
      required: false,
      default: ''
    },
    dark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Only used on touch screens; with a mouse the bar opens on :hover in CSS.
      expanded: false
    };
  },
  methods: {
    // The name goes home; on touch screens it opens and closes the menu instead,
    // since there's no hover there (Back still leads home).
    onBrandClick() {
      if (isTouch()) {
        this.expanded = !this.expanded;
        return;
      }
      this.$router.push({ name: 'HomePage' });
    },
    scrollToContact() {
      const contact = document.getElementById('contact');
      if (contact) {
        contact.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = 'mailto:Florinlica@hotmail.com';
      }
    },
    goBack() {
      if (this.$route.name === 'HomePage') {
        this.$router.push({ name: 'HelloWorld' });
      } else {
        this.$router.push({ name: 'HomePage' });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.home-header {
  position: sticky;
  top: 10px;
  z-index: 50;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  margin: 10px auto;
  padding: 8px 10px;
  width: 300px;
  max-width: calc(100% - 20px);
  height: 70px;
  border-radius: 10px;
  // Inverted against the page: light on the studio side, dark on the portfolio side.
  background: linear-gradient(90deg, var(--gray) 50%, var(--fluorine-card) 50%);
  box-shadow: 0 4px 0 0 rgba(0, 0, 0, 0.35);
  overflow: hidden;
  transition: width 0.6s ease;
  &.expanded {
    width: 900px;
  }
}
@media (hover: hover) {
  .home-header:hover {
    width: 900px;
  }
}

// Florin | logo | Lica, with Contact under Florin and Back under Lica,
// so the logo sits exactly on the seam between the two halves.
.center-content {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  grid-template-columns: 90px auto 90px;
  grid-template-rows: auto auto;
  column-gap: 12px;
  align-items: center;
  white-space: nowrap;
}
.first {
  grid-column: 1;
  justify-self: end;
  color: var(--black);
}
.last {
  grid-column: 3;
  justify-self: start;
  color: var(--fluorine);
}
.name {
  grid-row: 1;
  font-family: "Times New Roman";
  font-size: 24px;
  text-decoration: none;
}
.logo-link {
  grid-column: 2;
  grid-row: 1;
  display: flex;
}
.logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
// "Contact · Back" as one small line under the name, each word on its own half.
.links {
  grid-column: 1 / -1;
  grid-row: 2;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  column-gap: 6px;
  margin-top: 5px;
  .first,
  .last {
    grid-column: auto;
  }
}
.small {
  line-height: 1;
  padding: 0;
  border: none;
  background: none;
  font-family: "PT Sans Narrow", sans-serif;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  opacity: 0.75;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
  &.dot {
    justify-self: center;
    color: var(--darkgray);
    cursor: default;
  }
}

// One link per side, each filling the space between the edge and the name.
.side-link {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: calc(50% - 150px);
  display: flex;
  justify-content: center;
  // Hidden while the bar is closed; fades in once the bar has mostly widened.
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  .expanded > & {
    opacity: 1;
    pointer-events: auto;
    transition-delay: 0.35s;
  }
  @media (hover: hover) {
    .home-header:hover > & {
      opacity: 1;
      pointer-events: auto;
      transition-delay: 0.35s;
    }
  }
  &.studio {
    left: 10px;
  }
  &.work {
    right: 10px;
  }
  a {
    padding: 10px 18px;
    border-radius: 10px;
    font-family: "Times New Roman";
    font-size: 20px;
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.6s ease, color 0.6s ease;
  }
  &.studio a {
    color: var(--black);
    &:hover,
    &.router-link-active {
      background-color: var(--darkgray);
      color: var(--white);
    }
  }
  &.work a {
    color: var(--fluorine);
    &:hover,
    &.router-link-active {
      background-color: #000;
    }
  }
}

// Phones: name on top, then the two halves side by side underneath.
@media (max-width: 768px) {
  .home-header {
    flex-wrap: wrap;
    height: auto;
    min-height: 70px;
    &.expanded {
      width: calc(100% - 20px);
    }
  }
  .center-content {
    position: static;
    transform: none;
    order: -1;
    flex-basis: 100%;
    justify-content: center;
  }
  .side-link {
    position: static;
    transform: none;
    display: none;
    width: 50%;
    margin-top: 8px;
    .expanded > & {
      display: flex;
    }
    a {
      font-size: 17px;
      padding: 8px 10px;
    }
  }
}
</style>
